import {
  addWayPhoto,
  DropZone,
  getHeights,
  getHere,
  getPlan,
  getWay,
  getWayPhotos,
  InvalidInput,
  NotFound,
  removeWayPhoto,
  saveDrawnWay,
  setTrack,
  snapPath,
  type Anchor,
  type DrawMode,
  type Plan,
  type PlanStop,
  type WayPhoto,
} from '@trippath/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { MapPanel } from '../../components/map/MapPanel';
import { profileOf } from '../../components/way/WayTrack';
import { minutesSaid } from '../../lib/clock';
import type { LineKind, MapData, MapPin } from '../../lib/map';
import { joined, stretchOf, thinned, walkMinutes } from '../../lib/wayDraw';
import { day } from '../places/PlaceDetail';

type State =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failed' }
  | { kind: 'ready'; plan: Plan; stop: PlanStop; next: PlanStop | null };

/** A stretch between two anchors: its points, and how long the router says it takes. */
interface Segment {
  path: [number, number][];
  minutes: number | null;
}

/** A track read from a file, and the same put on the roads, when asked. */
interface FromFile {
  name: string;
  format: string;
  path: [number, number][];
  heights: (number | null)[];
  snapped: [number, number][] | null | 'failed';
}

const MODES: DrawMode[] = ['foot', 'bike', 'car', 'straight'];

/** How a drawn way is drawn on the map, by how it goes. */
const LINES: Record<DrawMode, LineKind> = {
  foot: 'foot',
  bike: 'bike',
  car: 'car',
  straight: 'other',
};

/** The files a way is read from. */
const ACCEPT = '.gpx,.kml,.kmz,.geojson,.json,.csv,.txt,.fit,.tcx';

/** The profile's box. */
const [WIDE, HIGH] = [1000, 120];

/** A stretch between two anchors, by how the way goes: along the router's roads, or straight. */
async function segmentOf(a: Anchor, b: Anchor, mode: DrawMode): Promise<Segment> {
  const straight: Segment = {
    path: [
      [a.lon, a.lat],
      [b.lon, b.lat],
    ],
    minutes: null,
  };
  if (mode === 'straight') return straight;
  const road = await getWay(a, b, mode).catch(() => null);
  return road === null
    ? straight
    : { path: road.path, minutes: mode === 'foot' ? null : road.minutes };
}

/**
 * A way drawn on the map or taken from a file, with its climb (the `WayDraw` mockup): its points
 * clicked one by one, the line along roads and paths between them, a point dragged; a recorded
 * track read and put on the roads; the photos taken along it placed beside it; its height along
 * it; kept as the stop's way on.
 */
export function WayDraw() {
  const { t } = useTranslation();
  const { id = '', stop: stopId = '' } = useParams();
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    let live = true;
    getPlan(id).then(
      (plan) => {
        if (!live) return;
        const at = plan.stops.findIndex((one) => one.id === stopId);
        const stop = plan.stops[at];
        setState(
          stop === undefined
            ? { kind: 'missing' }
            : { kind: 'ready', plan, stop, next: plan.stops[at + 1] ?? null },
        );
      },
      (error: unknown) => {
        if (live) setState({ kind: error instanceof NotFound ? 'missing' : 'failed' });
      },
    );
    return () => {
      live = false;
    };
  }, [id, stopId]);

  if (state.kind === 'loading') return <p className="page quiet">{t('trips.loading')}</p>;
  if (state.kind !== 'ready') {
    return (
      <div className="page">
        <p className="form-failed" role="alert">
          {t(state.kind === 'missing' ? 'trips.notFound' : 'trips.failed')}
        </p>
        <Link to="/">{t('trips.back')}</Link>
      </div>
    );
  }
  return <Drawing trip={id} stop={state.stop} next={state.next} />;
}

/** The way drawn, its file, its photos and its profile. */
function Drawing({
  trip,
  stop,
  next,
}: {
  trip: string;
  stop: PlanStop;
  next: PlanStop | null;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const kept = stop.track;
  const [name, setName] = useState(
    kept?.name ?? (next ? `${stop.city} → ${next.city}` : stop.city),
  );
  const [mode, setMode] = useState<DrawMode>(kept?.anchors?.mode ?? 'foot');
  const [anchors, setAnchors] = useState<Anchor[]>(kept?.anchors?.points ?? []);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [drawing, setDrawing] = useState(kept === null);
  const [file, setFile] = useState<FromFile | null>(null);
  const [snap, setSnap] = useState(true);
  const [heights, setHeights] = useState<{
    path: [number, number][];
    heights: (number | null)[];
  } | null>(null);
  const [photos, setPhotos] = useState<WayPhoto[]>([]);
  const [said, setSaid] = useState<'saved' | 'failed' | 'file' | null>(null);
  // The anchors as last drawn: the router asked for each stretch in turn.
  const asked = useRef(0);

  // The stretches between the anchors, asked again whenever they or how the way goes change.
  useEffect(() => {
    const turn = ++asked.current;
    void (async () => {
      const found: Segment[] = [];
      for (let at = 1; at < anchors.length; at++) {
        found.push(await segmentOf(anchors[at - 1] as Anchor, anchors[at] as Anchor, mode));
      }
      if (turn === asked.current) setSegments(found);
    })();
  }, [anchors, mode]);

  useEffect(() => {
    getWayPhotos(trip, stop.id).then(setPhotos, () => undefined);
  }, [trip, stop.id]);

  // The way shown: a file's, on the roads when asked and matched, else the drawn one.
  const fileLine =
    file === null ? null : snap && Array.isArray(file.snapped) ? file.snapped : file.path;
  const path = useMemo(
    () => fileLine ?? (segments.length > 0 ? joined(segments.map((one) => one.path)) : []),
    [fileLine, segments],
  );

  // The ground's height along it, asked once it rests.
  useEffect(() => {
    if (path.length < 2) return;
    const asking = thinned(path);
    const wait = setTimeout(() => {
      getHeights(asking).then(
        (found) => {
          setHeights({ path: asking, heights: found.heights });
        },
        () => undefined,
      );
    }, 400);
    return () => {
      clearTimeout(wait);
    };
  }, [path]);

  const own = file !== null && !(snap && Array.isArray(file.snapped));
  // A file's own heights come first; the ground's where it said none.
  const profile =
    own && file.heights.some((height) => height !== null)
      ? { path: file.path, heights: file.heights }
      : heights !== null && heights.path.length === thinned(path).length
        ? heights
        : null;
  const whole = stretchOf(profile?.path ?? path, profile?.heights ?? []);

  const add = (point: { lat: number; lon: number }) => {
    const anchor: Anchor = { lon: point.lon, lat: point.lat, name: '' };
    setAnchors((was) => [...was, anchor]);
    getHere(point).then(
      (here) => {
        const named = here.address?.split(',')[0] ?? '';
        setAnchors((was) => was.map((one) => (one === anchor ? { ...one, name: named } : one)));
      },
      () => undefined,
    );
  };

  const readFile = (picked: File) => {
    setSaid(null);
    setTrack(trip, stop.id, picked).then(
      (plan) => {
        const track = plan.stops.find((one) => one.id === stop.id)?.track ?? null;
        if (track === null) return;
        setFile({
          name: track.name,
          format: track.format,
          path: track.path,
          heights: track.heights,
          snapped: null,
        });
        setName(track.name);
        setDrawing(false);
        snapPath(track.path, mode === 'straight' ? 'foot' : mode).then(
          (found) => {
            setFile((was) => was && { ...was, snapped: found.path ?? 'failed' });
          },
          () => {
            setFile((was) => was && { ...was, snapped: 'failed' });
          },
        );
      },
      (error: unknown) => {
        setSaid(error instanceof InvalidInput ? 'file' : 'failed');
      },
    );
  };

  const save = () => {
    setSaid(null);
    const points =
      file === null
        ? anchors.map((one, at) => ({
            ...one,
            name: one.name || t('wayDraw.point', { n: at + 1 }),
          }))
        : path
            .filter((_, at) => at === 0 || at === path.length - 1)
            .map(([lon, lat]) => ({ lon, lat, name: '' }));
    const saved = profile ?? { path, heights: path.map(() => null) };
    saveDrawnWay(trip, stop.id, {
      name: name.trim() || stop.city,
      mode,
      points,
      path: saved.path,
      heights: saved.heights,
    }).then(
      () => {
        setSaid('saved');
      },
      () => {
        setSaid('failed');
      },
    );
  };

  const km = (metres: number) =>
    t('journeys.km', {
      km: (metres / 1000).toLocaleString(lang, { maximumFractionDigits: 1 }),
    });
  const label = (at: number) =>
    at === 0 ? 'A' : at === anchors.length - 1 && !drawing ? 'B' : String(at);
  const pins: MapPin[] = [
    ...(file === null
      ? anchors.map((anchor, at) => ({
          key: `anchor:${String(at)}`,
          label: anchor.name || t('wayDraw.point', { n: at + 1 }),
          lat: anchor.lat,
          lon: anchor.lon,
          emoji: label(at),
          colour: '#475569',
          draggable: true,
        }))
      : []),
    ...photos.flatMap((photo) =>
      photo.lat === null || photo.lon === null
        ? []
        : [
            {
              key: `photo:${photo.id}`,
              label:
                photo.takenAt === null
                  ? t('wayDraw.photoUnknown')
                  : t('wayDraw.photo', { when: day(photo.takenAt, lang) }),
              lat: photo.lat,
              lon: photo.lon,
              emoji: '📷',
              colour: '#0891b2',
            },
          ],
    ),
  ];
  const data: MapData = {
    // The way's two stops, the view fitted round them until a way is drawn.
    points: [stop, next].flatMap((one) =>
      one?.place
        ? [{ lat: one.place.lat, lon: one.place.lon, label: one.city, kind: 'stop' as const }]
        : [],
    ),
    lines: path.length >= 2 ? [{ kind: LINES[mode], path }] : [],
    pins,
  };
  const placed = photos.filter((photo) => photo.lat !== null).length;
  const unplaced = photos.length - placed;
  const shape =
    profile && profile.heights.some((height) => height !== null)
      ? profileOf(profile, WIDE, HIGH)
      : '';

  return (
    <div className="waydraw-page">
      <div className="waydraw-map">
        <MapPanel
          data={data}
          label={t('wayDraw.mapLabel', { from: stop.city })}
          cooperative={false}
          keepView
          closest={14}
          controls
          onSpot={drawing && file === null ? add : undefined}
          onPinMove={(key, point) => {
            const at = Number(key.replace('anchor:', ''));
            setAnchors((was) =>
              was.map((one, index) => (index === at ? { ...one, ...point } : one)),
            );
          }}
        />
        {file === null && (
          <div className="waydraw-tools">
            <div className="waydraw-box">
              <div className="waydraw-seg" role="group" aria-label={t('wayDraw.how')}>
                {MODES.map((one) => (
                  <button
                    key={one}
                    type="button"
                    aria-pressed={mode === one}
                    onClick={() => {
                      setMode(one);
                    }}
                  >
                    {t(`wayDraw.mode.${one}`)}
                  </button>
                ))}
              </div>
            </div>
            <div className="waydraw-box">
              <button
                type="button"
                className="button button-small"
                disabled={anchors.length === 0}
                onClick={() => {
                  setAnchors((was) => was.slice(0, -1));
                }}
              >
                {t('wayDraw.undo')}
              </button>
              <button
                type="button"
                className="button button-small button-primary"
                onClick={() => {
                  setDrawing(!drawing);
                }}
              >
                {drawing ? t('wayDraw.finish') : t('wayDraw.draw')}
              </button>
            </div>
            {drawing && <span className="waydraw-note">{t('wayDraw.hint')}</span>}
          </div>
        )}
      </div>

      <aside className="waydraw-right" aria-label={t('wayDraw.title', { from: stop.city })}>
        <div className="waydraw-sec">
          <span className="waydraw-caps">
            {t('wayDraw.caps', { day: day(stop.end, lang) })}
          </span>
          <label className="field">
            <span className="visually-hidden">{t('wayDraw.name')}</span>
            <input
              className="text-input waydraw-name"
              type="text"
              maxLength={100}
              aria-label={t('wayDraw.name')}
              value={name}
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
          </label>
          {file === null &&
            (anchors.length === 0 ? (
              <p className="quiet waydraw-small">{t('wayDraw.none')}</p>
            ) : (
              <ol className="waydraw-anchors" aria-label={t('wayDraw.anchors')}>
                {anchors.map((anchor, at) => {
                  const segment = segments[at];
                  const named = anchor.name || t('wayDraw.point', { n: at + 1 });
                  const part =
                    segment && stretchOf(segment.path, sliceOf(profile, segment.path));
                  return (
                    <li key={`${String(anchor.lon)}-${String(anchor.lat)}-${String(at)}`}>
                      <div className="waydraw-an">
                        <span
                          className={`waydraw-n${at === anchors.length - 1 ? ' waydraw-end' : ''}`}
                        >
                          {label(at)}
                        </span>
                        <span>{named}</span>
                        <button
                          type="button"
                          className="link-button waydraw-small"
                          aria-label={t('wayDraw.remove', { name: named })}
                          onClick={() => {
                            setAnchors((was) => was.filter((_, index) => index !== at));
                          }}
                        >
                          ×
                        </button>
                      </div>
                      {part && (
                        <div className="waydraw-seg-info">
                          {t('wayDraw.segment', {
                            km: km(part.metres),
                            up: part.ascent,
                            down: part.descent,
                            time: minutesSaid(
                              segment.minutes ?? walkMinutes(part.metres, part.ascent),
                            ),
                          })}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ol>
            ))}
        </div>
        <div className="waydraw-sec">
          <span className="waydraw-caps">{t('wayDraw.file')}</span>
          <DropZone
            label={t('wayDraw.drop')}
            choose={t('wayDraw.choose')}
            hint={t('wayDraw.kinds')}
            accept={ACCEPT}
            onFile={readFile}
          />
          {said === 'file' && (
            <p className="form-failed" role="alert">
              {t('wayDraw.fileFailed')}
            </p>
          )}
          {file !== null && (
            <>
              <div className="waydraw-file">
                <span className="waydraw-ext">{file.format.toUpperCase()}</span>
                <span>
                  <b>{file.name}</b>
                  <br />
                  <span className="waydraw-small quiet">
                    {t('wayDraw.fileInfo', {
                      km: km(stretchOf(file.path, []).metres),
                      points: file.path.length,
                    })}
                  </span>
                </span>
                <span className="tag tag-ok">{t('wayDraw.read')}</span>
              </div>
              <label className="waydraw-row">
                <span>
                  {t('wayDraw.snap')}
                  <small>{t('wayDraw.snapHow')}</small>
                </span>
                <input
                  type="checkbox"
                  role="switch"
                  checked={snap}
                  onChange={() => {
                    setSnap(!snap);
                  }}
                />
              </label>
              {file.snapped === 'failed' && snap && (
                <p className="quiet waydraw-small" role="status">
                  {t('wayDraw.snapFailed')}
                </p>
              )}
              <div className="waydraw-cmp">
                {(['asFile', 'onRoads'] as const).map((one) => {
                  const line =
                    one === 'asFile'
                      ? file.path
                      : Array.isArray(file.snapped)
                        ? file.snapped
                        : file.path;
                  return (
                    <button
                      key={one}
                      type="button"
                      aria-pressed={one === 'onRoads' ? snap : !snap}
                      onClick={() => {
                        setSnap(one === 'onRoads');
                      }}
                    >
                      <svg viewBox="0 0 100 60" aria-hidden="true">
                        <path d={shapeOf(line)} />
                      </svg>
                      {t(`wayDraw.${one}`)}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
        <div className="waydraw-sec">
          <span className="waydraw-caps">{t('wayDraw.photos')}</span>
          {photos.length === 0 ? (
            <p className="quiet waydraw-small">{t('wayDraw.noPhotos')}</p>
          ) : (
            <>
              <ul className="waydraw-thumbs">
                {photos.map((photo) => (
                  <li
                    key={photo.id}
                    className={photo.lat === null ? 'waydraw-unplaced' : undefined}
                  >
                    <img
                      src={photo.image}
                      alt={
                        photo.takenAt === null
                          ? t('wayDraw.photoUnknown')
                          : t('wayDraw.photo', { when: day(photo.takenAt, lang) })
                      }
                    />
                    {photo.lat !== null && <span aria-hidden="true">📍</span>}
                    <button
                      type="button"
                      className="waydraw-thumb-remove"
                      aria-label={t('wayDraw.removePhoto')}
                      onClick={() => {
                        removeWayPhoto(photo.image).then(
                          () => getWayPhotos(trip, stop.id).then(setPhotos),
                          () => undefined,
                        );
                      }}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
              <p className="waydraw-small">
                {placed > 0 && <span>{t('wayDraw.placed', { count: placed })} </span>}
                {unplaced > 0 && <span>{t('wayDraw.unplaced', { count: unplaced })}</span>}
              </p>
            </>
          )}
          <label className="button button-small waydraw-start">
            {t('wayDraw.addPhotos')}
            <input
              type="file"
              accept="image/*"
              multiple
              className="visually-hidden"
              onChange={(event) => {
                const files = [...(event.target.files as FileList)];
                event.target.value = '';
                void (async () => {
                  for (const one of files)
                    await addWayPhoto(trip, stop.id, one).catch(() => null);
                  setPhotos(await getWayPhotos(trip, stop.id).catch(() => photos));
                })();
              }}
            />
          </label>
        </div>
        <div className="waydraw-sec waydraw-last">
          {said === 'saved' && (
            <p className="waydraw-said" role="status">
              {t('wayDraw.saved', { name })}{' '}
              <Link to={`/trips/${trip}`}>{t('wayDraw.toPlan')}</Link>
            </p>
          )}
          {said === 'failed' && (
            <p className="form-failed" role="alert">
              {t('wayDraw.failed')}
            </p>
          )}
          <div className="waydraw-actions">
            <Link className="button button-quiet" to={`/trips/${trip}`}>
              {t('wayDraw.cancel')}
            </Link>
            <button
              type="button"
              className="button button-primary"
              disabled={path.length < 2}
              onClick={save}
            >
              {t('wayDraw.save')}
            </button>
          </div>
        </div>
      </aside>

      <section className="waydraw-elev" aria-label={t('wayDraw.profile')}>
        <div className="waydraw-stats">
          <span>
            {t('wayDraw.length')} <b>{km(whole.metres)}</b>
          </span>
          {whole.highest !== null && whole.lowest !== null && (
            <>
              <span>
                {t('wayDraw.ascent')} <b>↑ {whole.ascent} m</b>
              </span>
              <span>
                {t('wayDraw.descent')} <b>↓ {whole.descent} m</b>
              </span>
              <span>
                {t('wayDraw.highest')} <b>{whole.highest} m</b>
              </span>
              <span>
                {t('wayDraw.lowest')} <b>{whole.lowest} m</b>
              </span>
            </>
          )}
          <span className="quiet">{t('wayDraw.heightsFrom')}</span>
        </div>
        {shape === '' ? (
          <p className="quiet waydraw-small">{t('wayDraw.noHeights')}</p>
        ) : (
          <svg
            className="waydraw-profile"
            viewBox={`0 0 ${String(WIDE)} ${String(HIGH)}`}
            preserveAspectRatio="none"
            role="img"
            aria-label={t('wayDraw.profileSaid', { low: whole.lowest, high: whole.highest })}
          >
            <path d={shape} />
          </svg>
        )}
      </section>
    </div>
  );
}

/** The heights of the profile's points that lie on a stretch: its own, where the profile has them. */
function sliceOf(
  profile: { path: [number, number][]; heights: (number | null)[] } | null,
  stretch: [number, number][],
): (number | null | undefined)[] {
  if (profile === null) return [];
  const on = new Set(stretch.map(([lon, lat]) => `${String(lon)},${String(lat)}`));
  return profile.path.flatMap(([lon, lat], at) =>
    on.has(`${String(lon)},${String(lat)}`) ? [profile.heights[at]] : [],
  );
}

/** A path's shape in a box 100 wide and 60 high, for the thumbnails that compare two lines. */
function shapeOf(path: readonly [number, number][]): string {
  const lons = path.map(([lon]) => lon);
  const lats = path.map(([, lat]) => lat);
  const [west, east] = [Math.min(...lons), Math.max(...lons)];
  const [south, north] = [Math.min(...lats), Math.max(...lats)];
  const span = Math.max(east - west, north - south, 1e-9);
  return path
    .map(
      ([lon, lat], at) =>
        `${at === 0 ? 'M' : 'L'}${(6 + ((lon - west) / span) * 88).toFixed(1)} ${(54 - ((lat - south) / span) * 48).toFixed(1)}`,
    )
    .join(' ');
}
